import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'
import { Trans, useTranslation } from 'react-i18next'

export default function Login() {
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Fix #8: mode controls which view is shown
  const [mode, setMode] = useState('login') // 'login' | 'forgot' | 'sent'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const [resetEmail, setResetEmail] = useState('')
  const [resetLoading, setResetLoading] = useState(false)
  const [resetError, setResetError] = useState('')

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    setLoading(true)

    const { data, error: signInError } = await signIn(email, password)

    if (signInError) {
      setError(signInError.message)
      setLoading(false)
      return
    }

    // Fix #4: use .limit(1) before .maybeSingle() to avoid an error when
    // the user has multiple non-cancelled registrations.
    const [{ data: existing }, { data: openCamp }] = await Promise.all([
      supabase
        .from('registrations')
        .select('id')
        .eq('user_id', data.user.id)
        .not('payment_status', 'eq', 'Cancelled')
        .limit(1)
        .maybeSingle(),
      supabase
        .from('camps')
        .select('id')
        .eq('registration_open', true)
        .limit(1)
        .maybeSingle(),
    ])

    if (existing) {
      navigate('/my-registration')
    } else if (openCamp) {
      navigate('/register-camp')
    } else {
      navigate('/')
    }
  }

  async function handleForgotPassword(e) {
    e.preventDefault()
    setResetError('')
    setResetLoading(true)
    const { error: resetErr } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: window.location.origin + '/reset-password',
    })
    setResetLoading(false)
    if (resetErr) {
      setResetError(resetErr.message)
    } else {
      setMode('sent')
    }
  }

  /* ── Forgot-password view ─────────────────────────────────────────────── */
  if (mode === 'forgot') {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
            <div className="text-center mb-8">
              <span className="text-4xl">🔑</span>
              <h1 className="mt-3 text-2xl font-bold text-gray-900">{t('login.resetTitle')}</h1>
              <p className="mt-1 text-sm text-gray-500">
                {t('login.resetSubtitle')}
              </p>
            </div>

            {resetError && (
              <div className="mb-5 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                <span className="mt-0.5 shrink-0">⚠️</span>
                <span>{resetError}</span>
              </div>
            )}

            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="reset-email">
                  {t('login.emailAddress')}
                </label>
                <input
                  id="reset-email"
                  type="email"
                  required
                  autoComplete="email"
                  value={resetEmail}
                  onChange={e => setResetEmail(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-oasis-500 focus:outline-none focus:ring-2 focus:ring-oasis-200 transition"
                  placeholder="you@example.com"
                />
              </div>

              <button
                type="submit"
                disabled={resetLoading}
                className="w-full rounded-lg bg-oasis-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-oasis-700 focus:outline-none focus:ring-2 focus:ring-oasis-300 disabled:opacity-60 disabled:cursor-not-allowed transition"
              >
                {resetLoading ? t('login.sendingReset') : t('login.sendResetEmail')}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-500">
              <button
                onClick={() => { setMode('login'); setResetError('') }}
                className="font-medium text-oasis-500 hover:text-oasis-700 transition"
              >
                {t('login.backToSignIn')}
              </button>
            </p>
          </div>
        </div>
      </div>
    )
  }

  /* ── Reset email sent confirmation ────────────────────────────────────── */
  if (mode === 'sent') {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 text-center">
            <span className="text-5xl">📬</span>
            <h1 className="mt-4 text-2xl font-bold text-gray-900">{t('login.checkEmailTitle')}</h1>
            <p className="mt-2 text-sm text-gray-500 max-w-xs mx-auto">
              <Trans
                i18nKey="login.checkEmailBody"
                values={{ email: resetEmail }}
                components={{ strong: <span className="font-medium text-gray-700" /> }}
              />
            </p>
            <button
              onClick={() => { setMode('login'); setResetError('') }}
              className="mt-6 w-full sm:w-auto rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition"
            >
              {t('login.backToSignIn')}
            </button>
          </div>
        </div>
      </div>
    )
  }

  /* ── Login view ───────────────────────────────────────────────────────── */
  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-full bg-oasis-50 border-2 border-oasis-200 flex items-center justify-center mx-auto">
              <span className="text-oasis-700 font-black text-2xl leading-none">O</span>
            </div>
            <h1 className="mt-3 text-2xl font-bold text-gray-900">{t('login.welcomeBack')}</h1>
            <p className="mt-1 text-sm text-gray-500">{t('login.signInSubtitle')}</p>
          </div>

          {/* Error banner */}
          {error && (
            <div className="mb-5 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              <span className="mt-0.5 shrink-0">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">
                {t('login.emailAddress')}
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-oasis-500 focus:outline-none focus:ring-2 focus:ring-oasis-200 transition"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700" htmlFor="password">
                  {t('login.password')}
                </label>
                {/* Fix #8: forgot password link */}
                <button
                  type="button"
                  onClick={() => { setResetEmail(email); setMode('forgot') }}
                  className="text-xs text-oasis-500 hover:text-oasis-700 transition"
                >
                  {t('login.forgotPassword')}
                </button>
              </div>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-oasis-500 focus:outline-none focus:ring-2 focus:ring-oasis-200 transition"
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-oasis-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-oasis-700 focus:outline-none focus:ring-2 focus:ring-oasis-300 disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              {loading ? t('login.signingIn') : t('login.signIn')}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            {t('login.noAccount')}{' '}
            <Link to="/register" className="font-medium text-oasis-500 hover:text-oasis-700 transition">
              {t('login.createOne')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
